import type { ThemeAssetResolver } from '../../../sdk';
import type { AssetId } from '../../../shared/types/ids';

/**
 * One themed picture on a shell surface, addressed by ID (Slice E″).
 *
 * The shell's panels are the platform's, but the *art* on them belongs to whichever game is
 * running, so this takes the resolver and never a path: the same ID-only addressing the scenes use,
 * with the same fallback chain behind it (D-014). A theme that declares nothing for the role draws
 * the platform placeholder; a theme that declares a file that never arrived draws it too, because
 * the browser's broken-image icon is a rendering bug wearing a picture's clothes.
 *
 * It is decoration, and says so: `alt=""` and `aria-hidden` keep it out of the reading order, since
 * every one of these panels already carries its sentence in words. A picture that a screen reader
 * announced *instead* of the sentence would be a step backwards for the family that needs it most.
 *
 * Nothing here animates, at any motion setting: it is a still picture, which is what makes it safe
 * for ACC-02 and for PRV-05 without a special case.
 */
export function StageArt({
  assets,
  assetId,
  className,
}: {
  assets: ThemeAssetResolver | null;
  assetId: AssetId | null;
  className?: string;
}) {
  if (assets === null || assetId === null) return null;

  const resolved = assets.resolve(assetId);

  return (
    <img
      className={className ?? 'stage__art'}
      data-testid="stage-art"
      data-asset-id={assetId}
      data-used-fallback={resolved.usedFallback ? 'true' : 'false'}
      src={resolved.definition.source}
      alt=""
      aria-hidden="true"
      draggable={false}
    />
  );
}
